import React, { useEffect, useRef, useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';
import { FormattedBookmark } from '../../../hooks/queries/bookmarkTypes';
import { useUpdateBookmark } from '../../../hooks/queries/useBookmarks';
import { logger } from '../../../utils/logger';

interface BookmarkNoteProps {
  bookmark: FormattedBookmark;
  initialEditMode?: boolean;
  onEditComplete?: () => void;
}

export const BookmarkNote: React.FC<BookmarkNoteProps> = ({
  bookmark,
  initialEditMode = false,
  onEditComplete,
}) => {
  // Don't auto-focus when a new bookmark is created - only edit when explicitly clicked
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [noteText, setNoteText] = useState(bookmark.note || '');
  const inputRef = useRef<HTMLInputElement>(null);
  
  // React Query hook for bookmark updates
  const updateBookmarkMutation = useUpdateBookmark();

  const hasNote = !!bookmark.note;

  // Focus input only when explicitly entering edit mode via button click,
  // not when component initializes with initialEditMode prop
  useEffect(() => {
    if (isEditing && inputRef.current && initialEditMode === false) {
      // Only focus if we're in edit mode because the user clicked the edit button
      inputRef.current.focus();
    }
  }, [isEditing, initialEditMode]);

  // Update state only when initialEditMode prop changes
  useEffect(() => {
    setIsEditing(initialEditMode);
  }, [initialEditMode]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent card click
    setIsEditing(true);
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent card click
    setIsEditing(false);
    setNoteText(bookmark.note || '');
  };

  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent card click

    try {
      // Use a single mutation for all operations
      await updateBookmarkMutation.mutateAsync({
        topicId: bookmark.topicId,
        bookmarkId: bookmark.id,
        updates: {
          note: noteText.trim() === '' ? null : noteText
        }
      });

      setIsEditing(false);

      // Call the callback if provided
      if (onEditComplete) {
        onEditComplete();
      }
    } catch (error) {
      logger.error('Error saving bookmark note:', error, {
        bookmarkId: bookmark.id,
        topicId: bookmark.topicId,
        operation: 'saveBookmarkNote'
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveClick(e as unknown as React.MouseEvent);
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent parent card click
  };

  // The section to show when not editing (displaying the note)
  const noteDisplay = (
    <div className="relative group px-3 py-2 min-h-[32px] bg-amber-50 rounded-md">
      <p className="text-sm text-amber-900 pr-8 line-clamp-3 group-hover:line-clamp-none transition-all">
        {bookmark.note}
      </p>
      <button
        onClick={handleEditClick}
        className="absolute top-1/2 right-3 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity text-amber-600 hover:text-blue-500"
      >
        <Pencil size={16} strokeWidth={2} />
      </button>
    </div>
  );

  // The section to show when editing
  const noteEditor = (
    <div className="flex items-center gap-2 w-full max-w-full" onClick={e => e.stopPropagation()}>
      <input
        ref={inputRef}
        type="text"
        value={noteText}
        onChange={e => setNoteText(e.target.value)}
        onKeyDown={handleKeyDown}
        onClick={handleInputClick}
        className="flex-1 min-w-0 px-3 py-2 text-sm bg-amber-50 rounded-md focus:outline-none"
        placeholder="Add a note..."
      />
      {noteText.trim() && (
        <div className="flex-shrink-0 flex items-center gap-1.5">
          <button
            onClick={handleCancelClick}
            className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
          >
            <X size={14} />
          </button>
          <button
            onClick={handleSaveClick}
            className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
          >
            <Check size={14} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="mb-2">
      {isEditing ? (
        noteEditor
      ) : hasNote ? (
        noteDisplay
      ) : (
        <div
          onClick={handleEditClick}
          className="cursor-pointer px-3 h-8 flex items-center justify-center border border-dashed border-amber-200 rounded-md hover:bg-amber-50 transition-colors text-center"
        >
          <p className="text-sm text-amber-600">Click to add a note</p>
        </div>
      )}
    </div>
  );
};
